import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSACTION_TYPES = [
  'Any Transaction',
  'Invoice',
  'Receipt',
  'Custom_Invoice',
  'Refund',
  'Adjustment'
];

export default function WalletTransactions() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 30,
    total_count: 0,
    total_pages: 0
  });
  const [wallet, setWallet] = useState({ balance: 0, locked_funds: 0 });
  
  // Filters
  const [transactionType, setTransactionType] = useState('Any Transaction');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Transaction detail modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchTransactions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '30'
      });
      
      if (transactionType !== 'Any Transaction') {
        params.append('transaction_type', transactionType);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await axios.get(`${API}/wallet/transactions/detailed?${params.toString()}`);
      setTransactions(response.data.transactions);
      setPagination(response.data.pagination);
      setWallet(response.data.wallet);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [transactionType, searchQuery]);

  useEffect(() => {
    fetchTransactions(1);
  }, [fetchTransactions]);

  const handleSearch = () => {
    fetchTransactions(1);
  };

  const viewTransactionDetail = async (txn) => {
    try {
      const response = await axios.get(`${API}/wallet/transactions/${txn.id}`);
      setSelectedTransaction(response.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('Failed to fetch transaction details');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    const isNegative = amount < 0;
    const absAmount = Math.abs(amount);
    if (isNegative) {
      return `(${absAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
    }
    return absAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'Invoice': return 'bg-blue-50 text-blue-700';
      case 'Receipt': return 'bg-green-50 text-green-700';
      case 'Custom_Invoice': return 'bg-purple-50 text-purple-700';
      case 'Refund': return 'bg-orange-50 text-orange-700';
      case 'Adjustment': return 'bg-gray-50 text-gray-700';
      default: return 'bg-slate-50 text-slate-700';
    }
  };

  // Filter transactions by tab
  const filteredTransactions = transactions.filter(txn => {
    if (activeTab === 'all') return true;
    if (activeTab === 'credit') return txn.amount > 0;
    if (activeTab === 'debit') return txn.amount < 0;
    return true;
  });

  const tabs = [
    { id: 'all', label: 'All Transactions' },
    { id: 'credit', label: 'Credits' },
    { id: 'debit', label: 'Debits' }
  ];

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        {/* Page Header with Balance Card */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" data-testid="transactions-title">
              Transactions
            </h1>
            <p className="text-slate-500 mt-1">View your wallet transaction history</p>
          </div>
          
          {/* Wallet Balance Card - Without Add Funds button */}
          <Card className="bg-white border-0 shadow-sm w-full sm:w-auto sm:min-w-[240px]" data-testid="wallet-balance-card">
            <CardContent className="p-5">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-blue-600">Available Balance</p>
                  <p className="text-2xl font-bold text-slate-800">
                    INR {wallet.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-slate-500">Locked Funds</p>
                  <p className="text-xl font-bold text-slate-800">
                    INR {wallet.locked_funds.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-4 md:gap-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium transition-colors relative text-nowrap
                  ${activeTab === tab.id 
                    ? 'text-blue-600' 
                    : 'text-slate-500 hover:text-slate-700'
                  }
                `}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <Select value={transactionType} onValueChange={setTransactionType}>
                <SelectTrigger className="w-40 bg-white" data-testid="transaction-type-filter">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  {TRANSACTION_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9 w-40 md:w-64 bg-white"
                  data-testid="search-transactions"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="bg-white border-0 shadow-sm" data-testid="transactions-table-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="transactions-table">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Transaction ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Type</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 text-nowrap">Amount (INR)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredTransactions.map((txn) => (
                      <tr key={txn.id} data-testid={`transaction-row-${txn.id}`}>
                        <td className="px-4 py-3 font-mono text-sm text-slate-600">
                          {txn.billing_id || txn.id.substring(0, 8)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-800 max-w-[300px] truncate">
                          {txn.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                          {formatDate(txn.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${getTransactionTypeColor(txn.transaction_type || 'Invoice')}`}>
                            {txn.transaction_type || 'Invoice'}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-medium ${txn.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {txn.amount < 0 ? '-' : '+'}₹{formatAmount(Math.abs(txn.amount))}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => viewTransactionDetail(txn)}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            data-testid={`view-transaction-${txn.id}`}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total_count)} of {pagination.total_count}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchTransactions(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchTransactions(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Transaction Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Transaction Details
              </DialogTitle>
            </DialogHeader>
            {selectedTransaction && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Transaction ID</p>
                    <p className="font-mono text-sm">{selectedTransaction.id}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Billing ID</p>
                    <p className="font-mono text-sm">{selectedTransaction.billing_id || '-'}</p>
                  </div>
                  {selectedTransaction.order_id && (
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">Order ID</p>
                      <p className="font-mono text-sm text-blue-600">{selectedTransaction.order_id}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Transaction Type</p>
                    <Badge className={getTransactionTypeColor(selectedTransaction.transaction_type || 'Invoice')}>
                      {selectedTransaction.transaction_type || 'Invoice'}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Date & Time</p>
                    <p className="text-sm">{formatDate(selectedTransaction.created_at)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Amount</p>
                    <p className={`text-lg font-bold ${selectedTransaction.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {selectedTransaction.amount < 0 ? '-' : '+'}₹{formatAmount(Math.abs(selectedTransaction.amount))}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Balance After</p>
                    <p className="text-sm font-mono">₹{formatAmount(selectedTransaction.balance_after)}</p>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500 mb-1">Description</p>
                  <p className="text-sm bg-[#2563eb]/10 p-3 rounded">{selectedTransaction.description}</p>
                </div>

                {/* Order Details if available */}
                {selectedTransaction.order && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Order Details</p>
                    <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Product</span>
                        <span className="text-sm font-medium">{selectedTransaction.order.product_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Quantity</span>
                        <span className="text-sm">{selectedTransaction.order.quantity || 1}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Billing Cycle</span>
                        <span className="text-sm capitalize">{selectedTransaction.order.billing_cycle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Status</span>
                        <Badge variant="outline" className="capitalize">{selectedTransaction.order.status}</Badge>
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Details if available */}
                {selectedTransaction.client && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-slate-700 mb-3">Client Details</p>
                    <div className="bg-green-50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Company</span>
                        <span className="text-sm font-medium">{selectedTransaction.client.company_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Email</span>
                        <span className="text-sm">{selectedTransaction.client.email}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
